import classNames from 'classnames'
import { useApp } from '../../../hooks/useApp'
import { getCorrespondingPendantProductVariantIdsByMainProductVariantId } from '../../../utils'
import { useEffect, useRef } from 'react'
import { Text } from '../../ui/Text/Text'

interface Props {
  className?: string
}

export const ChainVariants = ({ className }: Props) => {
  const {
    mainProduct,
    linkedMainProducts,
    activeMainProductVariantId,
    activePendantProductVaraintIds,
    setActiveMainProductVariantId,
    setConfirmationPopupDisplayed,
    redirectingToNextPage,
    setRedirectingToNextPage,
    setBufferedMainProduct,
    setBufferedMainProductVariant,
    isMainProductSinglePendant
  } = useApp()
  const activeItemRef = useRef<HTMLLIElement>(null)

  const mainProducts = [mainProduct, ...linkedMainProducts]

  useEffect(() => activeItemRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' }), [])

  const lengthOptionWithValues = mainProduct.optionsWithValues.find(option => option.name.toLowerCase() === 'length')
  const chainVariantsByMetal = lengthOptionWithValues?.values.map(lengthValue => mainProduct.variants.find(v => v.options[lengthOptionWithValues.position - 1] === lengthValue))
  const availableChainVariantsByMetal = chainVariantsByMetal?.filter(chainVariant => chainVariant?.available)

  if (!availableChainVariantsByMetal || availableChainVariantsByMetal.length === 0) {
    return <p>No available chain variants.</p>
  }

  const wrapperClassName = isMainProductSinglePendant
    ? classNames('colors-swatch-wrapper', className)
    : undefined

  const listClassName = isMainProductSinglePendant
    ? 'colors-swatch__list'
    : classNames(
        'flex items-center gap-3 md-max:pb-2 overflow-x-auto',
        redirectingToNextPage && 'cursor-wait',
        className
      )

  return (
    <div className={wrapperClassName}>
      {isMainProductSinglePendant && <span className="colors-swatch__label">Size: </span>}
      <ul className={listClassName}>
        {availableChainVariantsByMetal!.map((productVariant) => {
          if (!productVariant) return null
          const { id, title, featuredImageUrl } = productVariant
          const isSeparateProduct = !mainProduct.variants.some((mainProductVariant) => mainProductVariant.id === id)

          let buttonTitle: string = title
          const matchedMainProduct = mainProducts.find((mainProduct) => mainProduct.variants.some((variant) => variant.id === id))
          if (matchedMainProduct) {
            buttonTitle = matchedMainProduct.title + ' - ' + title
          }

          if (!productVariant.available) {
            buttonTitle += ' is out of stock'
          }

          const handleClick = () => {
            if (redirectingToNextPage) return

            if (!isSeparateProduct) {
              setActiveMainProductVariantId(id)
              return
            }

            const linkedBuilderProduct = linkedMainProducts.find((linkedBuilderProduct) => linkedBuilderProduct.variants.some((variant) => variant.id === id))

            if (!linkedBuilderProduct) return

            setRedirectingToNextPage(true)

            const allPendantsAvailable = linkedBuilderProduct.metafields.pendantProducts
              .map((pendantProduct) => pendantProduct.id)
              .every((pendantProductId) => mainProduct.metafields.pendantProducts.map((pendantProduct) => pendantProduct.id).includes(pendantProductId))

            if (allPendantsAvailable) {
              const correspondingPendantProductVariantIds = getCorrespondingPendantProductVariantIdsByMainProductVariantId(
                linkedBuilderProduct,
                id,
                activePendantProductVaraintIds
              )
              const bset = [productVariant.id, ...correspondingPendantProductVariantIds].join('-')

              const url = new URL(window.location.href)
              url.pathname = linkedBuilderProduct.url
              url.searchParams.set('bset', bset)
              window.history.replaceState({}, '', url.href)

              window.reloadBuilder?.(linkedBuilderProduct)
              return
            }

            setBufferedMainProduct(linkedBuilderProduct)
            setBufferedMainProductVariant(productVariant)
            setConfirmationPopupDisplayed(true)
          }

          // @TODO: Fix types for ids
          const isActive = +id === +activeMainProductVariantId

          if (!isMainProductSinglePendant) {
            return (
              <li key={id} className="shrink-0" ref={isActive ? activeItemRef : null}>
                <button
                  className={classNames(
                    'border border-subdued rounded-sm',
                    isActive && 'border-solid',
                    redirectingToNextPage && 'cursor-wait',
                    !productVariant.available && 'opacity-30 cursor-not-allowed'
                  )}
                  onClick={handleClick}
                  aria-label={buttonTitle}
                  title={buttonTitle}
                  disabled={!productVariant.available}
                >
                  <img
                    className="w-[80px] h-[100px] object-cover"
                    src={featuredImageUrl}
                    alt={buttonTitle}
                    width="40"
                    height="50"
                  />
                </button>
              </li>
            )
          }

          return (
            <li key={id} className={classNames('colors-swatch__item', isActive && 'colors-swatch__item--active')} ref={isActive ? activeItemRef : null}>
              <button onClick={handleClick} aria-label={buttonTitle} title={buttonTitle} disabled={!productVariant.available}>
                <Text>{productVariant.options[lengthOptionWithValues!.position - 1]}"</Text>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
